import React, { useState, useEffect } from 'react'
import { Modal, Button, Spin, Typography, message, Tabs } from 'antd'
import { api } from '../../../../../services'
import CustomTree from './CustomTree'
import './CustomTree.scss'
import './MoveCopyModal.scss'
import { useTranslation } from 'react-i18next'

const { Text } = Typography
const { TabPane } = Tabs

/**
 * MoveCopyModal Component for Custom Tree
 * Props:
 * - visible: boolean
 * - mode: 'move' | 'copy'
 * - item: the item to move/copy (can be array or single item)
 * - onSuccess: () => void
 * - onCancel: () => void
 */
const MoveCopyModal = ({
  user,
  visible,
  mode = 'move',
  item,
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false)
  const [treeLoading, setTreeLoading] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [selectedLibraryType, setSelectedLibraryType] = useState('my')
  const [activeTab, setActiveTab] = useState('my')
  const [expandedIds, setExpandedIds] = useState([])
  const [loadingIds, setLoadingIds] = useState([])
  const [treeData, setTreeData] = useState({
    my: [],
    org: [],
    public: []
  })
  const isSuperAdmin = user && user.info && user.info.is_super_admin
  // Library type configurations
  const libraryConfigs = {
    my: { isActiveOrg: true },
    org: { isOrg: true },
    public: { isPublic: true }
  }

  // Fetch folders for a specific parent
  const fetchFolders = async (parentId = null, libraryType = 'my') => {
    try {
      const response = await api.library.getLibraryList({
        limit: 100,
        offset: 0,
        type: 'FOLDER',
        isCreator: true,
        parent_id: parentId,
        ...libraryConfigs[libraryType]
      })
      if (response && response.data && response.data.rows) {
        return response.data.rows.map(folder => ({
          id: folder.id,
          name: folder.name || folder.title,
          parentId: parentId,
          hasChildren: folder.counts && folder.counts.folders > 0,
          counts: folder.counts || {}
        }))
      }
      return []
    } catch (error) {
      return []
    }
  }

  // Load initial tree data
  const loadInitialData = async libraryType => {
    setTreeLoading(true)
    try {
      const rootFolders = await fetchFolders(null, libraryType)
      setTreeData(prev => ({
        ...prev,
        [libraryType]: rootFolders
      }))
    } catch (error) {
    } finally {
      setTreeLoading(false)
    }
  }

  // Load children for a specific node
  const loadChildren = async (nodeId, libraryType) => {
    if (loadingIds.includes(nodeId)) return
    setLoadingIds(prev => [...prev, nodeId])
    try {
      const children = await fetchFolders(nodeId, libraryType)
      setTreeData(prev => {
        const newTreeData = { ...prev }
        const updateNode = nodes => {
          return nodes.map(node => {
            if (node.id === nodeId) {
              return { ...node, children }
            } else if (node.children) {
              return { ...node, children: updateNode(node.children) }
            }
            return node
          })
        }
        newTreeData[libraryType] = updateNode(newTreeData[libraryType])
        return newTreeData
      })
    } catch (error) {
    } finally {
      setLoadingIds(prev => prev.filter(id => id !== nodeId))
    }
  }

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedFolderId(null)
      setSelectedLibraryType('my')
      setActiveTab('my')
      setExpandedIds([])
      setLoadingIds([])
      setTreeData({ my: [], org: [], public: [] })
      loadInitialData('my')
    }
  }, [visible])

  // Load data when tab changes
  useEffect(() => {
    if (visible && activeTab && !treeData[activeTab].length) {
      loadInitialData(activeTab)
    }
  }, [activeTab, visible])

  // Handle expand/collapse
  const handleExpand = async node => {
    if (expandedIds.includes(node.id)) {
      setExpandedIds(expandedIds.filter(id => id !== node.id))
    } else {
      setExpandedIds([...expandedIds, node.id])
      if (!node.children && node.hasChildren) {
        await loadChildren(node.id, activeTab)
      }
    }
  }

  // Handle selection
  const handleSelect = node => {
    if (node.isRoot || node.id === 'root') {
      setSelectedFolderId(null)
    } else {
      setSelectedFolderId(node.id)
    }
    setSelectedLibraryType(activeTab)
  }

  // Handle tab change
  const handleTabChange = key => {
    setActiveTab(key)
    setSelectedFolderId(null)
    setSelectedLibraryType(null)
    setExpandedIds([])
  }

  // Handle move/copy action
  const handleOk = async () => {
    if (selectedFolderId === undefined || !item) {
      message.warning('Please select a destination folder')
      return
    }
    setLoading(true)
    try {
      let mediaIds = []
      if (Array.isArray(item)) {
        mediaIds = item
      } else if (item && item.id) {
        mediaIds = [item.id]
      } else {
        mediaIds = [item]
      }
      // Determine target status based on selected library type
      let targetStatus = 'private'
      if (activeTab === 'my') {
        targetStatus = 'private'
      } else if (activeTab === 'org') {
        targetStatus = 'org'
      } else {
        targetStatus = 'public'
      }
      const payload = {
        mediaIds,
        targetFolderId: selectedFolderId,
        status: targetStatus
      }
      if (mode === 'move') {
        await api.library.moveData(payload)
      } else {
        await api.library.copyData(payload)
      }
      message.success(
        mode === 'move' ? 'Moved successfully!' : 'Copied successfully!'
      )
      if (onSuccess) onSuccess()
    } catch (error) {
      message.error(`Failed to ${mode}. Please try again.`)
    } finally {
      setLoading(false)
    }
  }

  // Render tree for each tab
  const renderTree = libraryType => {
    const data = treeData[libraryType] || []
    // Add root node as the first node
    const rootNode = {
      id: 'root',
      name: '/ (Root Folder)',
      hasChildren: false,
      isRoot: true
    }
    const nodes = [rootNode, ...data]
    return (
      <div className="tree-container">
        <Spin spinning={treeLoading}>
          <CustomTree
            data={nodes}
            onSelect={handleSelect}
            selectedId={selectedFolderId === null ? 'root' : selectedFolderId}
            onExpand={handleExpand}
            expandedIds={expandedIds}
            loadingIds={loadingIds}
          />
        </Spin>
      </div>
    )
  }
  const { t } = useTranslation()

  return (
    <Modal
      title={mode === 'move' ? t('v5:move_items') : t('v5:copy_items')}
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText={mode === 'move' ? t('v5:move_here') : t('v5:copy_here')}
      cancelText={t('v4:cancel')}
      confirmLoading={loading}
      okButtonProps={{
        disabled:
          selectedFolderId === undefined && selectedLibraryType !== activeTab
      }}
      width={600}
      style={{ top: 20 }}
      className="move-copy-modal"
      centered
    >
      <div style={{ marginBottom: 16 }}>
        <Text>{t('v5:select_destination_library_and_folder')}</Text>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
        destroyInactiveTabPane={false}
      >
        <TabPane tab={t('media:my_media')} key="my">
          {renderTree('my')}
        </TabPane>
        <TabPane tab={t('media:organization_library')} key="org">
          {renderTree('org')}
        </TabPane>
        {isSuperAdmin && (
          <TabPane tab={t('media:public_library')} key="public">
            {renderTree('public')}
          </TabPane>
        )}
      </Tabs>
    </Modal>
  )
}

export default MoveCopyModal
